import React from 'react';
import {
  NumberOutlined,
  UserOutlined,
  DeleteOutlined,
  BankOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';
import { Col, message, Row, Tag, Spin, Button } from 'antd';
import type { UploadFile } from 'antd/lib/upload/interface';
import {
  Dialog,
  Form,
  setValue,
  useEffectWithPrev,
} from '@owl-frontend/components';
import {
  ParticipantType,
  type Participant,
} from '../../../api/claims/interface';
import type { ClaimFile } from '../../../api/files/interface';
import { Locale, SessionContext } from '../../../context/AppProvider';
import { FeatureKeys } from '../../../data/session/interface';
import useFilesHook from '../../ClaimOverviewContainer/FilesListComponent/useFilesHook';
import useClaimsHook from '../useClaimsHook';
import styles from './SelfServiceClaimCreationModalComponent.module.scss';

const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB file size limit
const MAX_CLAIMANTS = 20;

const shortenFileName = (fileName: string) => {
  if (fileName.length <= 50) {
    return fileName;
  }

  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `${fileName.slice(0, 44)}...${fileName.slice(-3)}`;
  }

  const fileNameBeforeExt = fileName.slice(0, lastDotIndex);
  const extension = fileName.slice(lastDotIndex);
  return `${fileNameBeforeExt.slice(0, 41)}...${fileNameBeforeExt.slice(
    -3
  )}${extension}`;
};

export type FormValues = {
  noteId?: string;
  noteContent?: string;
};
interface Props {
  loading: boolean;
  visible: boolean;
  onUpdate(data: { dossierId: string; files?: File[]; notes?: string }): void;
  onSubmit(data: {
    claimants: Participant[];
    insured?: Participant;
    clientDossierId: string;
    files: File[];
    assignee?: string;
    notes?: string;
    dossierType?: string;
  }): void;
  onClose(): void;
  initialValues:
    | {
        clientDossierId: string;
        notes: string;
        dossierId: string;
        dossierType: string;
        firstName?: string;
        lastName?: string;
        data: {
          claimants: string[];
          insured: string;
        };
      }
    | undefined;
}

interface FormProps {
  values?: FormValues;
  loading: boolean;
  visible: boolean;
  isClientDossierIdUnique: boolean;
  onSubmit(values: FormValues): void;
  onClose(): void;
  onFileChange(fileList: UploadFile<File>[]): void;
  checkClientDossierIdUnique(data: { clientDossierId: string }): void;
  hasLargeFile: boolean;
  files: UploadFile[];
  prevFiles: ClaimFile[] | null;
  editingExistingClaim: boolean;
}

const SelfServiceClaimCreationForm: Form.MFC<FormProps> = ({
  loading,
  visible,
  isClientDossierIdUnique,
  onClose,
  onFileChange,
  files,
  prevFiles,
  editingExistingClaim,
  checkClientDossierIdUnique,
  // RFF
  form: rffForm,
  pristine,
  handleSubmit,
  hasLargeFile,
  values,
  initialValues,
  invalid,
}) => {
  const { messages } = React.useContext(Locale);
  const { features } = React.useContext(SessionContext);
  const intl = useIntl();
  const [invalidate, setInvalidate] = React.useState(0);

  const insuredOptions = React.useMemo(
    () => [
      {
        label: messages['claims.hub.self_service.create_modal.add_entity'],
        value: messages['claims.hub.self_service.create_modal.add_entity'],
      },
      ...(values?.claimants
        ?.filter((claimant) => claimant.name)
        ?.map((claimant) => ({
          label: claimant.name,
          value: claimant.name,
        })) || []),
    ],
    [values?.claimants, messages]
  );

  const availableDossierType = Object.entries(features)
    .filter(([key, value]) => key.startsWith('enabled_dossier_') && value)
    .map(([key]) => {
      const type = key.replace('enabled_dossier_', '');
      const label =
        messages[`claims.hub.self_service.dossier_type.${type}`] ?? type;
      return {
        value: type,
        label,
      };
    });

  const acceptedFiles = React.useMemo<{
    mimes: string;
    text: string[];
  }>(() => {
    const mimes = ['application/pdf', 'image/tiff'];
    const text = ['.pdf', '.tiff'];
    if (features[FeatureKeys.AudioEnabled]) {
      mimes.push('audio/mpeg');
      text.push('.mp3');
    }

    if (features[FeatureKeys.FilesUploadRtf]) {
      mimes.push('application/rtf');
      mimes.push('text/rtf');
      text.push('.rtf');
    }

    if (features[FeatureKeys.FilesUploadHtml]) {
      mimes.push('text/html');
      text.push('.html');
    }

    if (features[FeatureKeys.FilesUploadDocx]) {
      mimes.push(
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      );
      text.push('.docx');
    }

    return {
      mimes: mimes.join(','),
      text,
    };
  }, [features]);

  // force render
  // eslint-disable-next-line
  React.useEffect(() => {
    setInvalidate((d) => {
      return d + 1;
    });
  }, [isClientDossierIdUnique]);

  React.useEffect(() => {
    if (!visible) {
      rffForm.reset();
    }
    // eslint-disable-next-line
  }, [visible]);

  React.useEffect(() => {
    if (values['claimants'] && values['claimants'].length > 0) {
      return;
    }

    if (features[FeatureKeys.MultiParticipantClaims]) {
      rffForm.mutators.setValue('claimants', [
        { name: '', type: ParticipantType.ORGANIZATION },
      ]);
    } else {
      rffForm.mutators.setValue('claimants', [{ name: '' }]);
    }
  }, [features, rffForm, values]);

  return (
    <Dialog
      destroyOnHidden
      open={visible}
      size="medium"
      title={
        <div className={styles.dialogTitle}>
          {!editingExistingClaim
            ? messages['claims.hub.self_service.create_modal.title']
            : messages['claims.hub.self_service.create_modal.update']}
        </div>
      }
      onClose={onClose}
      footer={{
        onConfirmOpts: {
          label: messages['claims.hub.self_service.create_modal.confirm'],
          onClick: handleSubmit,
          disabled:
            loading ||
            (!initialValues && pristine && !isClientDossierIdUnique) ||
            files.length === 0 ||
            hasLargeFile ||
            invalid,
          loading,
        },
        onCancelOpts: { label: messages['shared.cancel'], onClick: onClose },
      }}
      className={styles.createCaseDialog}
    >
      <Spin size="large" spinning={loading}>
        <form onSubmit={handleSubmit}>
          <Row gutter={[8, 12]} style={{ alignItems: 'flex-start' }}>
            <Col span={18}>
              <Form.InputManaged
                prefix={<NumberOutlined />}
                key={`clientDossierId--${invalidate}`}
                placeholder={
                  messages['claims.hub.self_service.create_modal.case_id']
                }
                label={
                  <div className={styles.formLabel}>
                    {
                      messages[
                        'claims.hub.self_service.create_modal.case_id_required'
                      ]
                    }
                  </div>
                }
                name="clientDossierId"
                disabled={editingExistingClaim}
                className={styles.formInput}
                defaultValue={initialValues?.clientDossierId || ''}
                onChange={(e) => {
                  rffForm.mutators.setValue('clientDossierId', e.target.value);
                  checkClientDossierIdUnique({
                    clientDossierId: e.target.value,
                  });
                }}
                validate={Form.Validators.combineValidators([
                  Form.Validators.required(),
                  (value) => {
                    if (editingExistingClaim || !value) {
                      return undefined;
                    }

                    return isClientDossierIdUnique
                      ? undefined
                      : messages[
                          'claims.hub.self_service.create_modal.case_id_validation'
                        ];
                  },
                ])}
              />
            </Col>
            <Col span={6}>
              <Form.SelectAntdManaged
                key="dossierType"
                name="dossierType"
                defaultValue={initialValues?.dossierType || 'ltd'}
                options={availableDossierType}
                className={clsx(styles.formInput, styles.formSelect)}
              />
            </Col>
            <Col span={24}>
              <FieldArray
                name="claimants"
                validate={
                  !features[FeatureKeys.MultiParticipantClaims]
                    ? undefined
                    : Form.Validators.notEmpty()
                }
              >
                {({ fields }) => {
                  return (
                    <>
                      {fields.map((field, index) => (
                        <Row
                          key={`${field}.${index}`}
                          gutter={[0, 0]}
                          style={{ marginBottom: 8 }}
                        >
                          <Col span={24}>
                            {index === 0 && (
                              <div
                                className={clsx(
                                  styles.formLabel,
                                  styles.withMargin
                                )}
                              >
                                {
                                  messages[
                                    'claims.hub.self_service.create_modal.claimants_required'
                                  ]
                                }
                              </div>
                            )}
                            <Row gutter={[8, 0]}>
                              <Col
                                span={
                                  features[FeatureKeys.MultiParticipantClaims]
                                    ? index === 0 || editingExistingClaim
                                      ? 19
                                      : 18
                                    : 24
                                }
                              >
                                <Form.InputManaged
                                  prefix={
                                    values?.claimants?.[index]?.type ===
                                    ParticipantType.ORGANIZATION ? (
                                      <BankOutlined />
                                    ) : (
                                      <UserOutlined />
                                    )
                                  }
                                  key={`name-${index}`}
                                  placeholder={
                                    messages[
                                      'claims.hub.self_service.create_modal.name'
                                    ]
                                  }
                                  name={`${field}.name`}
                                  disabled={editingExistingClaim}
                                  className={styles.formInput}
                                  defaultValue=""
                                  validate={Form.Validators.required()}
                                />
                              </Col>
                              {features[FeatureKeys.MultiParticipantClaims] && (
                                <Col span={5}>
                                  <Form.SelectAntdManaged
                                    name={`${field}.type`}
                                    defaultValue={ParticipantType.PERSON}
                                    disabled={editingExistingClaim}
                                    options={[
                                      {
                                        label:
                                          messages[
                                            'claims.hub.self_service.create_modal.person'
                                          ],
                                        value: ParticipantType.PERSON,
                                      },
                                      {
                                        label:
                                          messages[
                                            'claims.hub.self_service.create_modal.organization'
                                          ],
                                        value: ParticipantType.ORGANIZATION,
                                      },
                                    ]}
                                  />
                                </Col>
                              )}
                              <Col span={1} style={{ paddingLeft: 0 }}>
                                {index !== 0 && !editingExistingClaim && (
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    style={{ padding: 0 }}
                                    onClick={() => {
                                      fields.remove(index);
                                    }}
                                  />
                                )}
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      ))}
                      {features[FeatureKeys.MultiParticipantClaims] &&
                        !editingExistingClaim &&
                        values?.claimants?.length <= MAX_CLAIMANTS && (
                          <Col span={24}>
                            <Button
                              type="primary"
                              onClick={() => {
                                fields.push({
                                  name: '',
                                });
                              }}
                            >
                              {
                                messages[
                                  'claims.hub.self_service.create_modal.add_claimant'
                                ]
                              }
                            </Button>
                          </Col>
                        )}
                    </>
                  );
                }}
              </FieldArray>
            </Col>
            {features[FeatureKeys.MultiParticipantClaims] && (
              <Col span={24}>
                <div className={clsx(styles.formLabel, styles.withMargin)}>
                  {
                    messages[
                      'claims.hub.self_service.create_modal.insured_required'
                    ]
                  }
                </div>
                <Row gutter={16} align="top">
                  <Col
                    span={
                      values?.insured ===
                      messages[
                        'claims.hub.self_service.create_modal.add_entity'
                      ]
                        ? 12
                        : 24
                    }
                  >
                    <Form.SelectAntdManaged
                      key="insured"
                      name="insured"
                      disabled={editingExistingClaim}
                      defaultValue={
                        initialValues?.data?.insured ||
                        messages[
                          'claims.hub.self_service.create_modal.add_entity'
                        ]
                      }
                      options={insuredOptions}
                      style={
                        values?.insured !==
                        messages[
                          'claims.hub.self_service.create_modal.add_entity'
                        ]
                          ? { width: '100%' }
                          : undefined
                      }
                    />
                  </Col>
                  {values?.insured ===
                    messages[
                      'claims.hub.self_service.create_modal.add_entity'
                    ] && (
                    <Col span={12}>
                      <Form.InputManaged
                        prefix={<UserOutlined />}
                        key="separateInsured"
                        placeholder={
                          messages[
                            'claims.hub.self_service.create_modal.insured'
                          ]
                        }
                        name="separateInsured"
                        disabled={editingExistingClaim}
                        defaultValue={''}
                        maxLength={50}
                        showCount={true}
                        validate={Form.Validators.required()}
                      />
                    </Col>
                  )}
                </Row>
              </Col>
            )}
            <Col span={24}>
              <Form.Upload
                data-testid="document-upload-input"
                className={styles.uploadItems}
                accept={acceptedFiles.mimes}
                onChange={onFileChange}
                fileList={files.map((file) => ({
                  ...file,
                  name: file.fileName || file.name,
                }))}
                label={
                  <div className={styles.formLabel}>
                    {
                      messages[
                        'claims.hub.self_service.create_modal.files_required'
                      ]
                    }
                  </div>
                }
                uploadText={intl.formatMessage(
                  {
                    id: 'claims.hub.self_service.create_modal.upload_text',
                  },
                  {
                    acceptedFilesText: [...acceptedFiles.text]
                      .slice(0, acceptedFiles.text.length - 1)
                      .join(', '),
                    lastAcceptedFilesText: [...acceptedFiles.text].pop(),
                  }
                )}
                multiple
                draggable
              />
            </Col>
            {prevFiles && (
              <div className={styles.prevFilesContainer}>
                <p className={styles.prevFilesText}>
                  {messages['claims.hub.self_service.update.previous_files']}
                </p>
                {prevFiles.map((file) => (
                  <Tag key={file.file_id} className={styles.fileTags}>
                    {shortenFileName(file.file_name)}
                  </Tag>
                ))}
              </div>
            )}
            <Col span={24}>
              <Form.InputManaged
                key="notes"
                placeholder={
                  messages[
                    'claims.hub.self_service.create_modal.notes_placeholder'
                  ]
                }
                name="notes"
                rows={3}
                label={
                  <div className={styles.formLabel}>
                    {messages['claims.hub.self_service.create_modal.notes']}
                  </div>
                }
                className={styles.formInput}
                defaultValue={initialValues?.notes || ''}
              />
            </Col>
          </Row>
        </form>
      </Spin>
    </Dialog>
  );
};

const SelfServiceClaimCreationFormManaged = Form.manage(
  SelfServiceClaimCreationForm
);

const SelfServiceClaimCreationModalComponent: React.FC<Props> = ({
  loading,
  visible,
  onSubmit,
  onUpdate,
  onClose,
  initialValues,
}) => {
  const { messages } = React.useContext(Locale);
  const { current, features } = React.useContext(SessionContext);
  const [files, setFiles] = React.useState<UploadFile[]>([]);
  const [
    {
      isClientDossierIdUnique,
      actions: {
        createSelfServiceClaim,
        updateClaimFiles,
        checkClientDossierIdUnique: checkClientDossierIdUniqueAction,
      },
    },
    handler,
  ] = useClaimsHook();
  const [hasLargeFile, setHasLargeFile] = React.useState(false);
  const [{ files: claimFiles }, fileHandler] = useFilesHook(
    initialValues?.dossierId ?? ''
  );

  const [messageApi, contextHolder] = message.useMessage();
  const onFileChange = React.useCallback(
    (fileList: UploadFile[]) => {
      if (!fileList) {
        setFiles([]);
        return;
      }
      const isTooLarge = fileList.some(
        (file) => file.size && file.size > MAX_FILE_SIZE
      );

      if (isTooLarge) {
        messageApi.error(
          messages['claims.hub.self_service.create_modal.file_size_exceeded']
        );
        setHasLargeFile(true);
      } else {
        setHasLargeFile(false);
      }
      setFiles(
        fileList.map((file) => {
          return {
            uid: file.uid,
            name: file.name,
            fileName: shortenFileName(file.name),
            status: 'done',
            originFileObj: file.originFileObj as File,
          } as UploadFile;
        })
      );
    },
    [setFiles, setHasLargeFile, messageApi, messages]
  );

  const checkClientDossierIdUniqueDebounced = useDebouncedCallback(
    (data: { clientDossierId: string }) => {
      handler.checkClientDossierIdUnique(data);
    },
    500
  );

  const handleSubmit = React.useCallback(
    (data) => {
      const extractedFiles = files.map((file) => file.originFileObj as File);
      if (initialValues && initialValues.dossierId) {
        onUpdate({
          dossierId: initialValues.dossierId,
          files: extractedFiles,
          notes: data.notes,
        });
      } else {
        const selectedClaimant = data.claimants?.find(
          (claimant) => claimant.name === data.insured
        );

        onSubmit({
          claimants: (data.claimants || []).map((claimant) => ({
            name: claimant.name,
            type: claimant.type || ParticipantType.PERSON,
          })),
          insured:
            data.insured &&
            ({
              name:
                data.insured ===
                messages['claims.hub.self_service.create_modal.add_entity']
                  ? data.separateInsured
                  : data.insured,
              type: selectedClaimant?.type || ParticipantType.ORGANIZATION,
            } as Participant),
          clientDossierId: data.clientDossierId,
          files: extractedFiles,
          assignee: current?.email,
          notes: data.notes,
          dossierType: data.dossierType,
        });
      }

      setFiles([]);
    },
    [files, initialValues, onUpdate, onSubmit, messages, current]
  );

  React.useEffect(() => {
    if (loading) {
      messageApi.loading(
        messages['claims.hub.self_service.create_modal.loading']
      );
    }
  }, [loading, messageApi, messages]);

  React.useEffect(() => {
    if (!initialValues) {
      return;
    }
    fileHandler.fetchFiles();
  }, [initialValues, fileHandler]);

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }
      if (createSelfServiceClaim.status === 'fulfilled') {
        onClose();
      }
    },
    [createSelfServiceClaim.status, messageApi, messages, onClose]
  );

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }

      if (updateClaimFiles.status === 'fulfilled') {
        onClose();
      }
    },
    [updateClaimFiles.status, messageApi, messages, onClose]
  );

  return (
    <>
      {contextHolder}
      <SelfServiceClaimCreationFormManaged
        loading={
          loading || checkClientDossierIdUniqueAction.status === 'pending'
        }
        isClientDossierIdUnique={isClientDossierIdUnique}
        editingExistingClaim={!!initialValues}
        visible={visible}
        onSubmit={handleSubmit}
        onClose={() => {
          setFiles([]);
          onClose();
        }}
        onFileChange={onFileChange}
        files={files}
        hasLargeFile={hasLargeFile}
        checkClientDossierIdUnique={checkClientDossierIdUniqueDebounced}
        initialValues={
          initialValues
            ? {
                ...initialValues,
                ...initialValues.data,
              }
            : {
                clientDossierId: '',
                notes: '',
                insured: !features[FeatureKeys.MultiParticipantClaims]
                  ? undefined
                  : messages['claims.hub.self_service.create_modal.add_entity'],
              }
        }
        prevFiles={
          initialValues?.dossierId && claimFiles
            ? Object.values(claimFiles)
            : null
        }
        mutators={{ setValue, ...arrayMutators }}
      />
    </>
  );
};

export default SelfServiceClaimCreationModalComponent;
